/**
 * Local-first sync engine.
 *
 * localStorage remains the working copy: the app reads and writes it exactly
 * as it always has, and works fully offline and signed out. When a user is
 * signed in, changes are mirrored to their PocketBase account and changes
 * from their other devices are merged back in.
 *
 * Merge strategy is last-write-wins per record, keyed by a client-generated
 * id and compared on `clientUpdated`. Records are never hard-deleted
 * remotely; a delete sets `deleted: true` so the tombstone can propagate.
 *
 * Two cursors, deliberately in different clock domains:
 *   - the PULL cursor is PocketBase's server-issued `updated` value, so a
 *     device with a skewed clock still sees everyone else's changes;
 *   - the PUSH cursor is a device-clock value compared only against this
 *     device's own `clientUpdated` stamps.
 * The branded LocalStamp / ServerStamp types keep the two from being mixed.
 */
import { ClientResponseError } from 'pocketbase';
import pb from './lib/pocketbase';
import { PB_CONFIGURED } from './config';
import {
  serverStamp,
  type AuthUser,
  type BaseRecord,
  type ExerciseRecord,
  type LocalStamp,
  type ServerStamp,
  type Store,
  type SyncStatusKind,
  type SyncStatusListener,
  type Workout,
  type WorkoutData,
  type WorkoutRecord,
} from './types';
import {
  ID_MAP_KEY,
  LAST_PULL_KEY,
  LAST_PUSH_KEY,
  SYNCED_USER_KEY,
  TOMBSTONE_KEY,
  ensureStamped as ensureStampedIn,
  exerciseToRecord,
  idMap,
  newId,
  nowStamp,
  recordToWorkout,
  saveIdMap,
  saveTombstones,
  tombstones,
  workoutToRecord,
} from './lib/sync-records';

// Re-exported so callers keep a single entry point for the sync engine.
export { newId, stampExercise, stampWorkout, tombstoneWorkout } from './lib/sync-records';


let store: Store | null = null;
let statusListener: SyncStatusListener | null = null;
let syncing = false;
let pending = false;
let debounceTimer: ReturnType<typeof setTimeout> | undefined;


function setStatus(text: string, kind: SyncStatusKind = 'idle'): void {
  statusListener?.(text, kind);
}

function requireStore(): Store {
  if (!store) throw new Error('Sync.bindStore() must be called before use.');
  return store;
}



// --- merge -------------------------------------------------------------

interface FoundWorkout {
  list: Workout[];
  index: number;
  name: string;
  workout: Workout;
}

function findLocalWorkout(data: WorkoutData, clientId: string): FoundWorkout | null {
  for (const [name, list] of Object.entries(data)) {
    if (!list) continue;
    const index = list.findIndex((w) => w.id === clientId);
    const workout = index >= 0 ? list[index] : undefined;
    if (workout) return { list, index, name, workout };
  }
  return null;
}

/** Mutable box so the cursor can be advanced across the pull and push passes. */
interface Cursor {
  value: ServerStamp;
}

function mergeRemote(
  remoteExercises: ExerciseRecord[],
  remoteWorkouts: WorkoutRecord[],
  seen: Set<string>,
  cursor: Cursor,
): boolean {
  const s = requireStore();
  const exercises = s.getExercises();
  const workoutData = s.getWorkoutData();
  const map = idMap();
  let changed = false;

  const noteCursor = (record: { updated?: ServerStamp }): void => {
    if (record.updated && record.updated > cursor.value) cursor.value = record.updated;
  };

  for (const record of remoteExercises) {
    map[record.clientId] = record.id;
    noteCursor(record);

    const local = exercises.find((ex) => ex.id === record.clientId);

    if (!local) {
      if (record.deleted) continue;
      // A same-named exercise created independently on this device should be
      // adopted rather than duplicated in the picker. This fires on every
      // first sign-in, because every device starts with the same three
      // defaults (pushups, pull-ups, chin-ups) under different client ids.
      //
      // Note the metrics are taken from the remote unconditionally, with no
      // clientUpdated comparison -- the one place that is not last-write-wins.
      // So if this device had customised, say, pushups to also track weight
      // and the account's copy tracks only reps, that customisation is lost.
      // Deliberately left as-is: it needs the same exercise customised
      // differently on two devices, the worst outcome is an input field
      // appearing or disappearing (re-added via Edit Exercise in seconds),
      // and no workout data is ever affected. If it ever needs fixing, union
      // the two metric sets here rather than picking a winner -- but keep the
      // steady-state branch below last-write-wins, or removing a metric could
      // never sync.
      const byName = exercises.find((ex) => ex.name === record.name);
      if (byName) {
        byName.id = record.clientId;
        byName.metrics = record.metrics ?? byName.metrics;
        byName.clientUpdated = record.clientUpdated;
      } else {
        exercises.push({
          id: record.clientId,
          name: record.name,
          metrics: record.metrics ?? ['reps'],
          clientUpdated: record.clientUpdated,
        });
      }
      workoutData[record.name] ??= [];
      seen.add(record.clientId);
      changed = true;
      continue;
    }

    if (record.clientUpdated > local.clientUpdated) {
      if (local.name !== record.name) {
        // Remote rename wins: move the local history to the new key.
        const moved = workoutData[local.name] ?? [];
        const target = workoutData[record.name] ?? [];
        workoutData[record.name] = target.concat(moved);
        delete workoutData[local.name];
        for (const w of workoutData[record.name] ?? []) w.exercise = record.name;
        local.name = record.name;
      }
      local.metrics = record.metrics ?? local.metrics;
      local.clientUpdated = record.clientUpdated;
      seen.add(record.clientId);
      changed = true;
    }
  }

  for (const record of remoteWorkouts) {
    map[record.clientId] = record.id;
    noteCursor(record);

    const found = findLocalWorkout(workoutData, record.clientId);

    if (!found) {
      if (record.deleted) continue;
      (workoutData[record.exercise] ??= []).push(recordToWorkout(record));
      seen.add(record.clientId);
      changed = true;
      continue;
    }

    if (record.clientUpdated > found.workout.clientUpdated) {
      if (record.deleted) {
        found.list.splice(found.index, 1);
      } else if (found.name !== record.exercise) {
        found.list.splice(found.index, 1);
        (workoutData[record.exercise] ??= []).push(recordToWorkout(record));
      } else {
        found.list[found.index] = recordToWorkout(record);
      }
      seen.add(record.clientId);
      changed = true;
    }
  }

  saveIdMap(map);
  return changed;
}

// --- push ---------------------------------------------------------------

async function pushLocal(
  since: LocalStamp | '',
  userId: string,
  seen: Set<string>,
  cursor: Cursor,
): Promise<void> {
  const s = requireStore();
  const map = idMap();

  const noteCursor = (record: { updated?: ServerStamp } | undefined): void => {
    if (record?.updated && record.updated > cursor.value) cursor.value = record.updated;
  };

  async function upsert(
    collection: string,
    clientId: string,
    data: Record<string, unknown>,
  ): Promise<void> {
    const remoteId = map[clientId];
    if (remoteId) {
      try {
        noteCursor(await pb.collection(collection).update<BaseRecord>(remoteId, data));
        return;
      } catch (err) {
        // The row was removed server-side; recreate it rather than losing it.
        if (!(err instanceof ClientResponseError) || err.status !== 404) throw err;
        delete map[clientId];
      }
    }
    const created = await pb.collection(collection).create<BaseRecord>(data);
    map[clientId] = created.id;
    noteCursor(created);
  }

  // Sequential on purpose: a phone on a weak connection copes far better with
  // a short queue than with a burst of parallel writes.
  for (const exercise of s.getExercises()) {
    if (seen.has(exercise.id)) continue;
    if (since && exercise.clientUpdated <= since) continue;
    await upsert('exercises', exercise.id, exerciseToRecord(exercise, userId));
  }

  for (const list of Object.values(s.getWorkoutData())) {
    for (const workout of list ?? []) {
      if (seen.has(workout.id)) continue;
      if (since && workout.clientUpdated <= since) continue;
      await upsert('workouts', workout.id, workoutToRecord(workout, userId));
    }
  }

  const stones = tombstones();
  for (const stone of stones) {
    const remoteId = map[stone.id];
    if (!remoteId) continue; // never reached the server, nothing to tombstone
    try {
      noteCursor(
        await pb.collection('workouts').update<BaseRecord>(remoteId, {
          deleted: true,
          clientUpdated: stone.clientUpdated,
        }),
      );
    } catch (err) {
      if (!(err instanceof ClientResponseError) || err.status !== 404) throw err;
    }
  }

  saveIdMap(map);

  // Clear only the tombstones this pass actually pushed. A delete made while
  // the sync was in flight is still pending and must survive.
  if (stones.length) {
    const pushed = new Set(stones.map((stone) => stone.id));
    saveTombstones(tombstones().filter((stone) => !pushed.has(stone.id)));
  }
}

// --- the sync cycle ------------------------------------------------------

export async function syncNow(): Promise<void> {
  if (!PB_CONFIGURED || !pb.authStore.isValid || !store) return;

  const user = pb.authStore.record;
  if (!user) return;

  if (syncing) {
    pending = true;
    return;
  }
  if (!navigator.onLine) {
    setStatus('Offline — will sync later', 'offline');
    return;
  }

  syncing = true;
  setStatus('Syncing…', 'busy');

  const lastPushed = (localStorage.getItem(LAST_PUSH_KEY) ?? '') as LocalStamp | '';
  const pushStartedAt = nowStamp();
  const seen = new Set<string>();
  const cursor: Cursor = {
    value: serverStamp(localStorage.getItem(LAST_PULL_KEY) ?? ''),
  };

  const scope = `user = "${user.id}"`;
  const filter = cursor.value ? `${scope} && updated > "${cursor.value}"` : scope;

  try {
    const remoteExercises = await pb
      .collection('exercises')
      .getFullList<ExerciseRecord>({ filter, sort: 'created' });
    const remoteWorkouts = await pb
      .collection('workouts')
      .getFullList<WorkoutRecord>({ filter, sort: 'created' });

    if (mergeRemote(remoteExercises, remoteWorkouts, seen, cursor)) {
      store.persist();
      store.onRemoteChange();
    }

    await pushLocal(lastPushed, user.id, seen, cursor);

    localStorage.setItem(LAST_PULL_KEY, cursor.value);
    localStorage.setItem(LAST_PUSH_KEY, pushStartedAt);
    localStorage.setItem(SYNCED_USER_KEY, user.id);
    setStatus('Synced', 'ok');
  } catch (err) {
    console.warn('[sync] failed', err);
    setStatus(
      navigator.onLine ? 'Sync failed — will retry' : 'Offline — will sync later',
      'error',
    );
  } finally {
    syncing = false;
    if (pending) {
      pending = false;
      schedule(300);
    }
  }
}

/** Coalesces the burst of saves the app makes when logging a workout. */
export function schedule(delay = 1500): void {
  if (!PB_CONFIGURED || !pb.authStore.isValid) return;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => void syncNow(), delay);
}

// --- account lifecycle ---------------------------------------------------

function clearSyncMetadata(): void {
  for (const key of [LAST_PULL_KEY, LAST_PUSH_KEY, ID_MAP_KEY, TOMBSTONE_KEY]) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing useful to do here.
    }
  }
}

export function resetLocalData(): void {
  const s = requireStore();
  s.setExercises([
    { name: 'pushups', metrics: ['reps'], id: newId(), clientUpdated: nowStamp() },
    { name: 'pull-ups', metrics: ['reps'], id: newId(), clientUpdated: nowStamp() },
    { name: 'chin-ups', metrics: ['reps'], id: newId(), clientUpdated: nowStamp() },
  ]);
  s.setWorkoutData({ pushups: [], 'pull-ups': [], 'chin-ups': [] });
  s.persist();
  clearSyncMetadata();
}

/**
 * Signing in on a device that already holds local data is ambiguous: it may
 * be this user's own work from before they had an account, or it may belong
 * to whoever used the device before them. Ask rather than guess -- guessing
 * wrong uploads one person's history into another person's account.
 */
function adoptLocalDataOnSignIn(userId: string): void {
  const s = requireStore();
  const previousUser = localStorage.getItem(SYNCED_USER_KEY);
  const hasLocalData = Object.values(s.getWorkoutData()).some(
    (list) => (list?.length ?? 0) > 0,
  );

  if (!hasLocalData || previousUser === userId) return;

  if (previousUser && previousUser !== userId) {
    const discardLocal = confirm(
      'This device has workout history that was last synced to a different account.\n\n' +
        "OK — discard it and load this account's workouts instead.\n" +
        'Cancel — keep it and merge it into this account.',
    );
    if (discardLocal) resetLocalData();
  }
}

export async function onSignedIn(user: AuthUser): Promise<void> {
  const s = requireStore();
  adoptLocalDataOnSignIn(user.id);
  // A fresh account pairing must re-examine every record, not just those
  // changed since some earlier account's sync.
  if (localStorage.getItem(SYNCED_USER_KEY) !== user.id) clearSyncMetadata();
  ensureStampedIn(s);
  s.persist();
  await syncNow();
}

export function onSignedOut(): void {
  clearSyncMetadata();
  setStatus('', 'idle');
}

// --- wiring --------------------------------------------------------------

export function bindStore(accessors: Store): void {
  store = accessors;
}

export function onStatus(listener: SyncStatusListener): void {
  statusListener = listener;
}

export function init(): void {
  const s = requireStore();
  if (ensureStampedIn(s)) s.persist();

  window.addEventListener('online', () => schedule(500));

  if (PB_CONFIGURED && pb.authStore.isValid) {
    pb.collection('users')
      .authRefresh()
      .then(() => syncNow())
      .catch(() => {
        // Token rejected -- drop it so the UI can prompt for a fresh sign-in.
        pb.authStore.clear();
        s.onAuthChange();
      });
  }
}

/** Backfills sync ids on records created before syncing existed. */
export function ensureStamped(): boolean {
  return ensureStampedIn(requireStore());
}
